// src/admin/pages/TestimonialsPage.jsx
import React, { useState } from "react";
import {
  Box, Button, Card, Typography, Chip, IconButton, Dialog, DialogTitle,
  DialogContent, DialogActions, TextField, MenuItem, CircularProgress,
  Skeleton, Snackbar, Alert, InputAdornment, Tooltip, Avatar,
  Table, TableHead, TableRow, TableCell, TableBody, Switch, FormControlLabel,
  TablePagination, Paper,
} from "@mui/material";
import {
  Plus, Pencil, Trash2, MessageSquare, User, Building2, MapPin, Star,
  Link2, Video, Search, Upload,
} from "lucide-react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useForm, Controller } from "react-hook-form";
import AdminShell from "../layout/AdminShell";
import {
  adminGetTestimonials,
  adminGetTestimonialById,
  adminCreateTestimonial,
  adminUpdateTestimonial,
  adminDeleteTestimonial,
  adminSetTestimonialStatus,
  adminSetTestimonialOrder,
} from "../../api/testimonials";

const LinkedinIcon = (props) => (
  <svg
    viewBox="0 0 24 24"
    width="16"
    height="16"
    fill="currentColor"
    {...props}
  >
    <path d="M4.98 3.5a2.5 2.5 0 1 1 0 5 2.5 2.5 0 0 1 0-5zM3 9h4v12H3V9zm7 0h3.8v1.7h.05c.53-1 1.83-2.05 3.77-2.05 4.03 0 4.78 2.65 4.78 6.1V21h-4v-5.6c0-1.34-.02-3.06-1.87-3.06-1.87 0-2.16 1.46-2.16 2.96V21h-4V9z" />
  </svg>
);

const VIDEO_TYPES = [
  { label: "No video", value: "none" },
  { label: "YouTube", value: "youtube" },
  { label: "Facebook", value: "facebook" },
  { label: "Vimeo", value: "vimeo" },
  { label: "External link", value: "external" },
];

const DEFAULT_VALUES = {
  name: "",
  designation: "",
  company: "",
  profileImage: "",
  location: "",
  testimonialText: "",
  rating: "",
  testimonialDate: "",
  videoType: "none",
  videoUrl: "",
  videoThumbnailUrl: "",
  linkedinUrl: "",
  profileUrl: "",
  featured: false,
  displayOrder: 0,
  isActive: true,
};

export default function TestimonialsPage() {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [editLoading, setEditLoading] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });
  const [searchQuery, setSearchQuery] = useState("");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const { register, handleSubmit, reset, control, watch, setValue, formState: { errors } } =
    useForm({ defaultValues: DEFAULT_VALUES });

  const notify = (message, severity = "success") => setSnackbar({ open: true, message, severity });
  const videoType = watch("videoType");

  // Fetch testimonials
  const { data: apiTestimonials = [], isLoading } = useQuery({
    queryKey: ["admin-testimonials"],
    queryFn: () => adminGetTestimonials().then((r) => r.data?.data ?? r.data),
  });
  const testimonials = Array.isArray(apiTestimonials) ? apiTestimonials : [];
  const filtered = testimonials.filter((t) =>
    t.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    t.company?.toLowerCase().includes(searchQuery.toLowerCase())
  );
  const paginated = filtered.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  // Mutations
  const createMutation = useMutation({
    mutationFn: (data) => adminCreateTestimonial(data),
    onSuccess: () => {
      qc.invalidateQueries(["admin-testimonials"]);
      notify("Testimonial created");
      closeDialog();
    },
    onError: (err) => notify(err?.response?.data?.message || "Couldn't create testimonial", "error"),
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }) => adminUpdateTestimonial(id, data),
    onSuccess: () => {
      qc.invalidateQueries(["admin-testimonials"]);
      notify("Testimonial updated");
      closeDialog();
    },
    onError: (err) => notify(err?.response?.data?.message || "Couldn't update testimonial", "error"),
  });

  const deleteMutation = useMutation({
    mutationFn: (id) => adminDeleteTestimonial(id),
    onSuccess: () => {
      qc.invalidateQueries(["admin-testimonials"]);
      notify("Testimonial deleted");
      setConfirmDelete(null);
    },
    onError: (err) => {
      notify(err?.response?.data?.message || "Couldn't delete testimonial", "error");
      setConfirmDelete(null);
    },
  });

  const statusMutation = useMutation({
    mutationFn: ({ id, isActive }) => adminSetTestimonialStatus(id, isActive),
    onSuccess: () => {
      qc.invalidateQueries(["admin-testimonials"]);
      notify("Status updated");
    },
    onError: (err) => notify(err?.response?.data?.message || "Couldn't update status", "error"),
  });

  const orderMutation = useMutation({
    mutationFn: ({ id, displayOrder }) => adminSetTestimonialOrder(id, displayOrder),
    onSuccess: () => {
      qc.invalidateQueries(["admin-testimonials"]);
      notify("Display order updated");
    },
    onError: (err) => notify(err?.response?.data?.message || "Couldn't update display order", "error"),
  });

  const saving = createMutation.isPending || updateMutation.isPending;

  // Handle form submission
  const onSubmit = async (values) => {
    const formData = new FormData();

    // Append all fields to FormData
    Object.keys(values).forEach((key) => {
      if (values[key] !== null && values[key] !== undefined) {
        formData.append(key, values[key]);
      }
    });

    try {
      if (editing) {
        await updateMutation.mutateAsync({ id: editing.id, data: formData });
      } else {
        await createMutation.mutateAsync(formData);
      }
    } catch (err) {
      notify(err?.response?.data?.message || "Couldn't save testimonial", "error");
    }
  };

  // Open dialog for creating a new testimonial
  const openCreate = () => {
    setEditing(null);
    reset(DEFAULT_VALUES);
    setOpen(true);
  };

  // Open dialog for editing a testimonial
  const openEdit = async (t) => {
    setEditLoading(true);
    try {
      const { data } = await adminGetTestimonialById(t.id);
      const record = data?.data ?? data;
      setEditing(record);
      reset({ ...DEFAULT_VALUES, ...record });
      setOpen(true);
    } catch (err) {
      notify(err?.response?.data?.message || "Couldn't load testimonial", "error");
    } finally {
      setEditLoading(false);
    }
  };

  // Close dialog
  const closeDialog = () => {
    if (saving) return;
    setOpen(false);
    setEditing(null);
    reset(DEFAULT_VALUES);
  };

  // Handle image upload
  const handleImageUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setValue("profileImage", file);
  };

  // Handle display order change
  const handleDisplayOrderChange = (id, newOrder) => {
    orderMutation.mutate({ id, displayOrder: newOrder });
  };

  return (
    <AdminShell title="Testimonials">
      {/* Header */}
      <Box sx={{
        display: "flex",
        alignItems: { xs: "flex-start", sm: "center" },
        justifyContent: "space-between",
        flexDirection: { xs: "column", sm: "row" },
        gap: 1.5,
        mb: 2.5
      }}>
        <Typography sx={{ fontSize: 13.5, color: "text.secondary", maxWidth: 480 }}>
          Testimonials shown on the public site. Written text, photo, or video — set status to Active to publish.
        </Typography>
        <Button
          variant="contained"
          startIcon={<Plus size={18} />}
          onClick={openCreate}
          sx={{
            borderRadius: "10px",
            textTransform: "none",
            fontWeight: 700,
            bgcolor: "#2563EB",
            boxShadow: "0 10px 24px -12px rgba(37,99,235,0.5)",
            "&:hover": { bgcolor: "#1D4ED8" }
          }}
        >
          Add Testimonial
        </Button>
      </Box>

      {/* Search Bar */}
      <Card sx={{ borderRadius: "16px", border: "1px solid rgba(15,23,42,0.08)", p: 2, mb: 2.5 }}>
        <TextField
          fullWidth
          placeholder="Search testimonials by name or company…"
          value={searchQuery}
          onChange={(e) => { setSearchQuery(e.target.value); setPage(0); }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search size={18} color="#94A3B8" />
              </InputAdornment>
            )
          }}
          sx={{ "& .MuiOutlinedInput-root": { borderRadius: "10px" } }}
        />
      </Card>

      {/* Testimonials Table */}
      <Card sx={{ borderRadius: "16px", border: "1px solid rgba(15,23,42,0.08)", overflow: "hidden" }}>
        <Box sx={{ overflowX: "auto" }}>
          <Table>
            <TableHead>
              <TableRow sx={{ bgcolor: "#F8FAFC" }}>
                {["Person", "Company", "Video", "Rating", "Order", "Status", ""].map((h) => (
                  <TableCell
                    key={h}
                    sx={{
                      fontWeight: 700,
                      fontSize: 12,
                      textTransform: "uppercase",
                      color: "text.secondary",
                      borderBottom: "1px solid rgba(15,23,42,0.08)",
                      whiteSpace: "nowrap"
                    }}
                  >
                    {h}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {isLoading ? (
                Array.from({ length: 6 }).map((_, i) => (
                  <TableRow key={i}>
                    {Array.from({ length: 7 }).map((__, j) => (
                      <TableCell key={j}>
                        <Skeleton variant="text" width={j === 0 ? 160 : 70} />
                      </TableCell>
                    ))}
                  </TableRow>
                ))
              ) : paginated.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} align="center" sx={{ py: 8 }}>
                    <Box sx={{
                      width: 52,
                      height: 52,
                      borderRadius: "14px",
                      bgcolor: "rgba(37,99,235,0.07)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      mx: "auto",
                      mb: 1.5
                    }}>
                      <MessageSquare size={22} color="#2563EB" />
                    </Box>
                    <Typography fontWeight={700} color="#0F172A" mb={0.5}>
                      No testimonials found
                    </Typography>
                    <Typography color="text.secondary" fontSize={13.5}>
                      {searchQuery ? "Try adjusting your search" : "Add your first testimonial."}
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                paginated.map((t) => (
                  <TableRow key={t.id} hover sx={{ "&:last-child td": { borderBottom: 0 } }}>
                    <TableCell sx={{ minWidth: 220 }}>
                      <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
                        <Avatar src={t.profileImage || undefined} sx={{ width: 38, height: 38 }}>
                          {t.name?.[0]}
                        </Avatar>
                        <Box sx={{ minWidth: 0 }}>
                          <Typography sx={{ fontWeight: 700, fontSize: 13.75, color: "#0F172A" }} noWrap>
                            {t.name}
                          </Typography>
                          <Typography sx={{ fontSize: 12, color: "text.secondary" }} noWrap>
                            {t.designation || "—"}
                          </Typography>
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell sx={{ fontSize: 13.5, color: "text.secondary" }}>
                      {t.company || "—"}
                    </TableCell>
                    <TableCell>
                      {t.videoType && t.videoType !== "none" ? (
                        <Chip
                          icon={<Video size={12} />}
                          label={t.videoType}
                          size="small"
                          sx={{
                            fontWeight: 600,
                            fontSize: 11,
                            height: 22,
                            bgcolor: "rgba(124,58,237,0.08)",
                            color: "#7C3AED"
                          }}
                        />
                      ) : (
                        <Typography sx={{ fontSize: 13, color: "text.secondary" }}>—</Typography>
                      )}
                    </TableCell>
                    <TableCell sx={{ fontSize: 13.5 }}>
                      {t.rating != null ? `${t.rating}/5` : "—"}
                    </TableCell>
                    <TableCell sx={{ fontSize: 13.5, color: "text.secondary" }}>
                      <TextField
                        type="number"
                        value={t.displayOrder ?? 0}
                        onChange={(e) => handleDisplayOrderChange(t.id, parseInt(e.target.value, 10) || 0)}
                        size="small"
                        sx={{ width: 60 }}
                      />
                    </TableCell>
                    <TableCell>
                      <FormControlLabel
                        control={
                          <Switch
                            size="small"
                            checked={!!t.isActive}
                            onChange={(e) => statusMutation.mutate({ id: t.id, isActive: e.target.checked })}
                          />
                        }
                        label={
                          <Typography sx={{
                            fontSize: 12,
                            fontWeight: 600,
                            color: t.isActive ? "#16A34A" : "#94A3B8"
                          }}>
                            {t.isActive ? "Active" : "Inactive"}
                          </Typography>
                        }
                      />
                    </TableCell>
                    <TableCell align="right">
                      <Box sx={{ display: "flex", gap: 0.75, justifyContent: "flex-end" }}>
                        <Tooltip title="Edit">
                          <span>
                            <IconButton
                              size="small"
                              onClick={() => openEdit(t)}
                              disabled={editLoading}
                              sx={{ border: "1px solid rgba(15,23,42,0.1)", borderRadius: "8px" }}
                            >
                              {editLoading ? <CircularProgress size={14} /> : <Pencil size={14} />}
                            </IconButton>
                          </span>
                        </Tooltip>
                        <Tooltip title="Delete">
                          <IconButton
                            size="small"
                            onClick={() => setConfirmDelete(t)}
                            sx={{
                              border: "1px solid rgba(15,23,42,0.1)",
                              borderRadius: "8px",
                              "&:hover": { borderColor: "#EF4444", color: "#EF4444" }
                            }}
                          >
                            <Trash2 size={14} />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </Box>
        {!isLoading && (
          <TablePagination
            rowsPerPageOptions={[5, 10, 25, 50]}
            component="div"
            count={filtered.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={(e, p) => setPage(p)}
            onRowsPerPageChange={(e) => {
              setRowsPerPage(parseInt(e.target.value, 10));
              setPage(0);
            }}
            sx={{ borderTop: "1px solid rgba(15,23,42,0.08)" }}
          />
        )}
      </Card>

      {/* Add/Edit Testimonial Dialog */}
      <Dialog
        open={open}
        onClose={closeDialog}
        maxWidth="md"
        fullWidth
        PaperProps={{ sx: { borderRadius: "24px", maxHeight: "90vh" } }}
      >
        <DialogTitle sx={{
          py: 2.5,
          px: 3,
          background: "linear-gradient(135deg, #1D4ED8 0%, #2563EB 55%, #3B82F6 100%)"
        }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.75 }}>
            <Box sx={{
              width: 44,
              height: 44,
              borderRadius: "12px",
              bgcolor: "rgba(255,255,255,0.18)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center"
            }}>
              <MessageSquare size={22} color="#FFFFFF" />
            </Box>
            <Box>
              <Typography sx={{ fontWeight: 800, fontSize: 21, color: "#FFFFFF" }}>
                {editing ? "Edit Testimonial" : "Add New Testimonial"}
              </Typography>
              <Typography sx={{ fontSize: 13.5, color: "rgba(255,255,255,0.85)", mt: 0.25 }}>
                {editing ? `Editing: ${editing.name}` : "Create a testimonial for your site"}
              </Typography>
            </Box>
          </Box>
        </DialogTitle>

        <Box component="form" onSubmit={handleSubmit(onSubmit)}>
          <DialogContent sx={{
            display: "flex",
            flexDirection: "column",
            gap: 2.5,
            pt: 3,
            pb: 1,
            px: 3,
            maxHeight: "65vh",
            overflowY: "auto",
            backgroundColor: "#F1F5F9"
          }}>
            {/* Person Section */}
            <Paper elevation={0} sx={{
              p: 3,
              borderRadius: "16px",
              border: "1px solid rgba(15,23,42,0.06)",
              borderLeft: "4px solid #2563EB",
              bgcolor: "#FFFFFF"
            }}>
              <Typography sx={{ fontSize: 15.5, fontWeight: 800, color: "#0F172A", mb: 2.5 }}>
                Person
              </Typography>
              <Box sx={{ display: "grid", gridTemplateColumns: "repeat(12, 1fr)", gap: 2.5 }}>
                <Box sx={{ gridColumn: { xs: "span 12", sm: "span 6" } }}>
                  <TextField
                    label="Name"
                    fullWidth
                    required
                    disabled={saving}
                    error={!!errors.name}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <User size={16} color="#94A3B8" />
                        </InputAdornment>
                      )
                    }}
                    InputLabelProps={{ shrink: true }}
                    {...register("name", { required: true })}
                  />
                </Box>
                <Box sx={{ gridColumn: { xs: "span 12", sm: "span 6" } }}>
                  <TextField
                    label="Designation"
                    fullWidth
                    disabled={saving}
                    InputLabelProps={{ shrink: true }}
                    {...register("designation")}
                  />
                </Box>
                <Box sx={{ gridColumn: { xs: "span 12", sm: "span 6" } }}>
                  <TextField
                    label="Company"
                    fullWidth
                    disabled={saving}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <Building2 size={16} color="#94A3B8" />
                        </InputAdornment>
                      )
                    }}
                    InputLabelProps={{ shrink: true }}
                    {...register("company")}
                  />
                </Box>
                <Box sx={{ gridColumn: { xs: "span 12", sm: "span 6" } }}>
                  <TextField
                    label="Location"
                    fullWidth
                    disabled={saving}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <MapPin size={16} color="#94A3B8" />
                        </InputAdornment>
                      )
                    }}
                    InputLabelProps={{ shrink: true }}
                    {...register("location")}
                  />
                </Box>
                <Box sx={{ gridColumn: "span 12" }}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                    <Avatar
                      src={typeof watch("profileImage") === "string" ? watch("profileImage") : undefined}
                      sx={{ width: 56, height: 56 }}
                    >
                      {watch("name")?.[0]}
                    </Avatar>
                    <Button
                      component="label"
                      variant="outlined"
                      size="small"
                      disabled={saving}
                      startIcon={<Upload size={14} />}
                      sx={{ textTransform: "none", fontWeight: 600, borderRadius: "8px" }}
                    >
                      Upload profile photo
                      <input type="file" accept="image/*" hidden onChange={handleImageUpload} />
                    </Button>
                    <Typography sx={{ fontSize: 12.5, color: "text.secondary" }} noWrap>
                      {watch("profileImage") instanceof File
                        ? watch("profileImage").name
                        : watch("profileImage") || "No file selected"}
                    </Typography>
                  </Box>
                </Box>
              </Box>
            </Paper>

            {/* Testimonial Section */}
            <Paper elevation={0} sx={{
              p: 3,
              borderRadius: "16px",
              border: "1px solid rgba(15,23,42,0.06)",
              borderLeft: "4px solid #16A34A",
              bgcolor: "#FFFFFF"
            }}>
              <Typography sx={{ fontSize: 15.5, fontWeight: 800, color: "#0F172A", mb: 2.5 }}>
                Testimonial
              </Typography>
              <Box sx={{ display: "grid", gridTemplateColumns: "repeat(12, 1fr)", gap: 2.5 }}>
                <Box sx={{ gridColumn: "span 12" }}>
                  <TextField
                    label="Testimonial Text"
                    fullWidth
                    required
                    multiline
                    rows={4}
                    disabled={saving}
                    error={!!errors.testimonialText}
                    InputLabelProps={{ shrink: true }}
                    {...register("testimonialText", { required: true })}
                  />
                </Box>
                <Box sx={{ gridColumn: { xs: "span 6", sm: "span 4" } }}>
                  <TextField
                    label="Rating"
                    fullWidth
                    disabled={saving}
                    type="number"
                    inputProps={{ min: 0, max: 5, step: 0.1 }}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <Star size={16} color="#94A3B8" />
                        </InputAdornment>
                      )
                    }}
                    InputLabelProps={{ shrink: true }}
                    {...register("rating", { min: 0, max: 5 })}
                  />
                </Box>
                <Box sx={{ gridColumn: { xs: "span 6", sm: "span 4" } }}>
                  <TextField
                    label="Date"
                    fullWidth
                    disabled={saving}
                    type="date"
                    InputLabelProps={{ shrink: true }}
                    {...register("testimonialDate")}
                  />
                </Box>
                <Box sx={{ gridColumn: { xs: "span 12", sm: "span 4" } }}>
                  <TextField
                    label="LinkedIn URL"
                    fullWidth
                    disabled={saving}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <LinkedinIcon size={16} color="#94A3B8" />
                        </InputAdornment>
                      )
                    }}
                    InputLabelProps={{ shrink: true }}
                    {...register("linkedinUrl")}
                  />
                </Box>
                <Box sx={{ gridColumn: "span 12" }}>
                  <TextField
                    label="Other Profile URL"
                    fullWidth
                    disabled={saving}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <Link2 size={16} color="#94A3B8" />
                        </InputAdornment>
                      )
                    }}
                    InputLabelProps={{ shrink: true }}
                    {...register("profileUrl")}
                  />
                </Box>
              </Box>
            </Paper>

            {/* Video Section */}
            <Paper elevation={0} sx={{
              p: 3,
              borderRadius: "16px",
              border: "1px solid rgba(15,23,42,0.06)",
              borderLeft: "4px solid #7C3AED",
              bgcolor: "#FFFFFF"
            }}>
              <Typography sx={{ fontSize: 15.5, fontWeight: 800, color: "#0F172A", mb: 2.5 }}>
                Video (optional)
              </Typography>
              <Box sx={{ display: "grid", gridTemplateColumns: "repeat(12, 1fr)", gap: 2.5 }}>
                <Box sx={{ gridColumn: { xs: "span 12", sm: "span 4" } }}>
                  <Controller
                    name="videoType"
                    control={control}
                    render={({ field }) => (
                      <TextField
                        select
                        label="Video Type"
                        fullWidth
                        disabled={saving}
                        InputLabelProps={{ shrink: true }}
                        {...field}
                        value={field.value ?? "none"}
                      >
                        {VIDEO_TYPES.map((v) => (
                          <MenuItem key={v.value} value={v.value}>
                            {v.label}
                          </MenuItem>
                        ))}
                      </TextField>
                    )}
                  />
                </Box>

                {["youtube", "facebook", "vimeo", "external"].includes(videoType) && (
                  <Box sx={{ gridColumn: { xs: "span 12", sm: "span 8" } }}>
                    <TextField
                      label="Video URL"
                      fullWidth
                      disabled={saving}
                      placeholder="https://…"
                      InputLabelProps={{ shrink: true }}
                      {...register("videoUrl")}
                    />
                  </Box>
                )}

                {videoType !== "none" && (
                  <Box sx={{ gridColumn: "span 12" }}>
                    <TextField
                      label="Video Thumbnail URL (optional)"
                      fullWidth
                      disabled={saving}
                      InputLabelProps={{ shrink: true }}
                      {...register("videoThumbnailUrl")}
                    />
                  </Box>
                )}
              </Box>
            </Paper>

            {/* Display Section */}
            <Paper elevation={0} sx={{
              p: 3,
              borderRadius: "16px",
              border: "1px solid rgba(15,23,42,0.06)",
              borderLeft: "4px solid #0891B2",
              bgcolor: "#FFFFFF"
            }}>
              <Typography sx={{ fontSize: 15.5, fontWeight: 800, color: "#0F172A", mb: 2.5 }}>
                Display
              </Typography>
              <Box sx={{ display: "grid", gridTemplateColumns: "repeat(12, 1fr)", gap: 2.5 }}>
                <Box sx={{ gridColumn: { xs: "span 12", sm: "span 4" } }}>
                  <TextField
                    label="Display Order"
                    fullWidth
                    disabled={saving}
                    type="number"
                    inputProps={{ min: 0 }}
                    InputLabelProps={{ shrink: true }}
                    {...register("displayOrder", { min: 0 })}
                  />
                </Box>
                <Box sx={{ gridColumn: { xs: "span 12", sm: "span 4" } }}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={!!watch("featured")}
                        onChange={(e) => setValue("featured", e.target.checked)}
                        disabled={saving}
                      />
                    }
                    label="Featured"
                    sx={{ mt: 1 }}
                  />
                </Box>
                <Box sx={{ gridColumn: { xs: "span 12", sm: "span 4" } }}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={!!watch("isActive")}
                        onChange={(e) => setValue("isActive", e.target.checked)}
                        disabled={saving}
                      />
                    }
                    label="Active (visible on site)"
                    sx={{ mt: 1 }}
                  />
                </Box>
              </Box>
            </Paper>
          </DialogContent>

          {/* Dialog Actions */}
          <DialogActions sx={{
            px: 3,
            py: 2.25,
            borderTop: "1px solid rgba(15,23,42,0.08)",
            bgcolor: "#FFFFFF"
          }}>
            <Button
              onClick={closeDialog}
              disabled={saving}
              sx={{
                textTransform: "none",
                fontWeight: 700,
                color: "#475569",
                borderRadius: "10px",
                bgcolor: "rgba(15,23,42,0.04)"
              }}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="contained"
              disabled={saving}
              startIcon={saving ? <CircularProgress size={16} color="inherit" /> : null}
              sx={{
                textTransform: "none",
                fontWeight: 700,
                borderRadius: "10px",
                background: "linear-gradient(135deg, #2563EB 0%, #1D4ED8 100%)",
                px: 3.5,
                minWidth: 140
              }}
            >
              {saving ? "Saving..." : editing ? "Update Testimonial" : "Create Testimonial"}
            </Button>
          </DialogActions>
        </Box>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={!!confirmDelete}
        onClose={() => !deleteMutation.isPending && setConfirmDelete(null)}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: { borderRadius: "18px" } }}
      >
        <DialogTitle sx={{ pb: 1, borderBottom: "1px solid rgba(15,23,42,0.08)" }}>
          <Typography sx={{ fontWeight: 700, fontSize: 17 }}>
            Delete this testimonial?
          </Typography>
        </DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <Typography sx={{ fontSize: 13.75, color: "text.secondary" }}>
            "{confirmDelete?.name}"'s testimonial will be removed from the public site immediately.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button onClick={() => setConfirmDelete(null)} disabled={deleteMutation.isPending}>
            Cancel
          </Button>
          <Button
            onClick={() => deleteMutation.mutate(confirmDelete.id)}
            disabled={deleteMutation.isPending}
            variant="contained"
            sx={{
              bgcolor: "#EF4444",
              borderRadius: "10px",
              "&:hover": { bgcolor: "#DC2626" }
            }}
          >
            {deleteMutation.isPending ? "Deleting..." : "Delete"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for Notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={3500}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert
          onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
          severity={snackbar.severity}
          variant="filled"
          sx={{ borderRadius: "10px" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </AdminShell>
  );
}